import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { affiliateService } from '../../services/affiliateService';

interface AffiliateDetail {
  id: string;
  contact_name?: string;
  company_name?: string;
  email: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
  website?: string;
  notes?: string;
  custom_commission_rate?: number | null;
  referral_code: string;
  referral_link: string;
  payment_method?: string;
  payment_details?: string;
  total_sales?: number;
  total_commission?: number;
  conversion_rate?: number;
}

interface CommissionHistory {
  id: string;
  created_at: string;
  order_id: string;
  order_amount: number;
  commission_amount: number;
  status: 'paid' | 'pending' | 'approved' | 'rejected' | 'flagged' | 'void' | 'processed';
}

interface TrafficData {
  date: string;
  visits: number;
  conversions: number;
  conversion_rate: number;
}

interface AffiliateDetailsProps {
  programId?: string;
  affiliateId?: string;
  onBack?: () => void;
}

export const AffiliateDetails: React.FC<AffiliateDetailsProps> = ({ programId, affiliateId, onBack }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [affiliate, setAffiliate] = useState<AffiliateDetail | null>(null);
  const [commissionHistory, setCommissionHistory] = useState<CommissionHistory[]>([]);
  const [trafficData, setTrafficData] = useState<TrafficData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState<Partial<AffiliateDetail>>({});

  // Fetch affiliate data from API
  useEffect(() => {
    if (!programId) {
      setError('No program ID provided');
      setLoading(false);
      return;
    }

    if (!affiliateId) {
      setError('No affiliate ID provided');
      setLoading(false);
      return;
    }

    const fetchAffiliateData = async () => {
      try {
        setLoading(true);
        setError(null);

        const detailsResp = await affiliateService.getAffiliateDetails(programId, affiliateId);
        const a = detailsResp?.affiliate || null;
        if (!a) throw new Error('Affiliate not found');

        // normalize referral_link if backend doesn't provide it
        const referral_link = a.referral_link || (a.referral_code ? `${window.location.origin}/?ref=${a.referral_code}` : '');

        const normalizedAffiliate: AffiliateDetail = {
          ...a,
          referral_link,
        };

        setAffiliate(normalizedAffiliate);
        setFormData(normalizedAffiliate);

        const commissionsResp = await affiliateService.getAffiliateCommissions(programId, affiliateId);
        const commissions = Array.isArray((commissionsResp as any)?.commissions) ? (commissionsResp as any).commissions : [];
        setCommissionHistory(
          commissions.map((c: any) => ({
            id: c.id,
            created_at: c.created_at,
            order_id: c.order_id,
            order_amount: Number(c.amount || 0),
            commission_amount: Number(c.commission_amount || 0),
            status: c.status,
          }))
        );

        const trafficResp = await affiliateService.getAffiliateTraffic(programId, affiliateId, 30);
        setTrafficData(Array.isArray((trafficResp as any)?.traffic) ? (trafficResp as any).traffic : []);
      
      } catch (error) {
        console.error('Error fetching affiliate data:', error);
        setError(error instanceof Error ? error.message : 'An unknown error occurred');
        toast.error('Failed to load affiliate data');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAffiliateData();
  }, [affiliateId, programId]);

  const handleCopyLink = () => {
    if (!affiliate) return;
    
    navigator.clipboard.writeText(affiliate.referral_link)
      .then(() => {
        toast.success('Referral link copied to clipboard!');
      })
      .catch(err => {
        console.error('Failed to copy link: ', err);
        toast.error('Failed to copy link to clipboard');
      });
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!programId || !affiliateId) return;
    try {
      const updates: any = {
        contact_name: formData.contact_name,
        company_name: formData.company_name,
        email: formData.email,
        website: formData.website,
        notes: formData.notes,
      };
      if (formData.custom_commission_rate !== undefined) {
        updates.custom_commission_rate = formData.custom_commission_rate;
      }

      const resp = await affiliateService.updateAffiliate(programId, affiliateId, updates);
      const updated = resp?.affiliate;
      if (updated) {
        const referral_link = updated.referral_link || (updated.referral_code ? `${window.location.origin}/?ref=${updated.referral_code}` : '');
        setAffiliate({ ...(updated as any), referral_link } as any);
        setFormData({ ...(updated as any), referral_link } as any);
      }
      setIsEditing(false);
      toast.success('Affiliate details updated successfully');
    } catch (err) {
      console.error(err);
      toast.error(err instanceof Error ? err.message : 'Failed to update affiliate');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!affiliate) {
    return (
      <div className="bg-white shadow overflow-hidden sm:rounded-lg p-6">
        <p>No affiliate found with ID: {affiliateId}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">{affiliate.contact_name}</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">{affiliate.email}</p>
          </div>
          <div>
            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
              ${affiliate.status === 'approved' ? 'bg-green-100 text-green-800' : 
                affiliate.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                'bg-red-100 text-red-800'}`}>
              {affiliate.status.charAt(0).toUpperCase() + affiliate.status.slice(1)}
            </span>
          </div>
        </div>
        
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex" aria-label="Tabs">
            {['profile', 'commissions', 'traffic', 'links'].map((tab) => (
              <button
                key={tab}
                className={`${
                  activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                } whitespace-nowrap py-4 px-4 border-b-2 font-medium text-sm`}
                onClick={() => setActiveTab(tab)}
              >
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </nav>
        </div>
        
        {/* Content for Profile Tab */}
        {activeTab === 'profile' && (
          <div className="p-6">
            <div className="flex justify-end mb-4">
              <button
                type="button"
                onClick={() => setIsEditing(!isEditing)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                {isEditing ? 'Cancel' : 'Edit Profile'}
              </button>
            </div>
            
            {isEditing ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact_name" className="block text-sm font-medium text-gray-700">Name</label>
                    <input
                      type="text"
                      name="contact_name"
                      id="contact_name"
                      value={formData.contact_name || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  {/* Phone field removed: not in AffiliateDetail schema */}
                  
                  <div>
                    <label htmlFor="website" className="block text-sm font-medium text-gray-700">Website</label>
                    <input
                      type="text"
                      name="website"
                      id="website"
                      value={formData.website || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="company_name" className="block text-sm font-medium text-gray-700">Company</label>
                    <input
                      type="text"
                      name="company_name"
                      id="company_name"
                      value={formData.company_name || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                  
                  {/* Address field removed: not in AffiliateDetail schema */}
                  
                  <div>
                    <label htmlFor="payment_method" className="block text-sm font-medium text-gray-700">Payment Method</label>
                    <select
                      id="payment_method"
                      name="payment_method"
                      value={formData.payment_method || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    >
                      <option value="PayPal">PayPal</option>
                      <option value="Bank Transfer">Bank Transfer</option>
                      <option value="Stripe">Stripe</option>
                    </select>
                  </div>
                  
                  <div>
                    <label htmlFor="payment_details" className="block text-sm font-medium text-gray-700">Payment Details</label>
                    <input
                      type="text"
                      name="payment_details"
                      id="payment_details"
                      value={formData.payment_details || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                      placeholder="PayPal email, bank account, etc."
                    />
                  </div>
                  
                  <div className="sm:col-span-2">
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700">Notes</label>
                    <textarea
                      id="notes"
                      name="notes"
                      rows={3}
                      value={formData.notes || ''}
                      onChange={handleFormChange}
                      className="mt-1 block w-full shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-300 rounded-md"
                    />
                  </div>
                </div>
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            ) : (
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">ID</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.id}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Contact Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.contact_name}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Email</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.email}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Website</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <a href={affiliate.website} className="text-blue-600 hover:text-blue-900" target="_blank" rel="noopener noreferrer">
                      {affiliate.website}
                    </a>
                  </dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Date joined</dt>
                  <dd className="mt-1 text-sm text-gray-900">{new Date(affiliate.created_at).toLocaleDateString()}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Company</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.company_name}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Payment method</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.payment_method}</dd>
                </div>
                <div className="sm:col-span-1">
                  <dt className="text-sm font-medium text-gray-500">Payment details</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.payment_details}</dd>
                </div>
                <div className="sm:col-span-2">
                  <dt className="text-sm font-medium text-gray-500">Notes</dt>
                  <dd className="mt-1 text-sm text-gray-900">{affiliate.notes}</dd>
                </div>
              </dl>
            )}
          </div>
        )}
        
        {/* Content for Commissions Tab */}
        {activeTab === 'commissions' && (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Amount</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissionHistory.map((commission) => (
                  <tr key={commission.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(commission.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {commission.order_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${commission.order_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      ${commission.commission_amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                        ${commission.status === 'paid' ? 'bg-green-100 text-green-800' : 
                          commission.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 
                          'bg-blue-100 text-blue-800'}`}>
                        {commission.status.charAt(0).toUpperCase() + commission.status.slice(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Content for Traffic Tab */}
        {activeTab === 'traffic' && (
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900">Traffic Overview</h4>
              <p className="mt-1 text-sm text-gray-500">
                Traffic and conversion data for the last 30 days.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-sm font-medium text-gray-500">Total Visits</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {trafficData.reduce((sum, data) => sum + data.visits, 0)}
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-sm font-medium text-gray-500">Total Conversions</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {trafficData.reduce((sum, data) => sum + data.conversions, 0)}
                  </p>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow">
                  <p className="text-sm font-medium text-gray-500">Avg. Conversion Rate</p>
                  <p className="mt-1 text-3xl font-semibold text-gray-900">
                    {(trafficData.length ? (trafficData.reduce((sum, data) => sum + data.conversion_rate, 0) / trafficData.length) : 0).toFixed(2)}%
                  </p>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Visits</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conversions</th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Conv. Rate</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {trafficData.map((data) => (
                    <tr key={data.date}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(data.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {data.visits}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {data.conversions}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {data.conversion_rate.toFixed(2)}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-6 text-right">
              <button
                type="button"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Export Report
              </button>
            </div>
          </div>
        )}
        
        {/* Content for Links Tab */}
        {activeTab === 'links' && (
          <div className="p-6">
            <div className="mb-6">
              <h4 className="text-lg font-medium text-gray-900">Affiliate Links</h4>
              <p className="mt-1 text-sm text-gray-500">
                Manage and share affiliate referral links.
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="mb-4">
                <label htmlFor="referral_code" className="block text-sm font-medium text-gray-700">Referral Code</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="referral_code"
                    id="referral_code"
                    value={affiliate.referral_code}
                    readOnly
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-md sm:text-sm border-gray-300"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="referral_link" className="block text-sm font-medium text-gray-700">Referral Link</label>
                <div className="mt-1 flex rounded-md shadow-sm">
                  <input
                    type="text"
                    name="referral_link"
                    id="referral_link"
                    value={affiliate.referral_link}
                    readOnly
                    className="flex-1 min-w-0 block w-full px-3 py-2 rounded-l-md sm:text-sm border-gray-300"
                  />
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    className="inline-flex items-center px-3 py-2 rounded-r-md border border-l-0 border-gray-300 bg-gray-50 text-gray-500 text-sm"
                  >
                    Copy
                  </button>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h5 className="font-medium text-gray-900">Banner Links</h5>
                <p className="mt-1 text-sm text-gray-500 mb-4">
                  Use these banner images with your referral link.
                </p>
                
                <div className="space-y-4">
                  <div className="border rounded-md p-2">
                    <div className="bg-gray-200 h-16 flex items-center justify-center text-gray-400 text-sm mb-2">
                      [Banner Image 300x250]
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">300x250</span>
                      <button className="text-xs text-blue-600">Copy HTML</button>
                    </div>
                  </div>
                  
                  <div className="border rounded-md p-2">
                    <div className="bg-gray-200 h-12 flex items-center justify-center text-gray-400 text-sm mb-2">
                      [Banner Image 728x90]
                    </div>
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">728x90</span>
                      <button className="text-xs text-blue-600">Copy HTML</button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
                <h5 className="font-medium text-gray-900">Text Links</h5>
                <p className="mt-1 text-sm text-gray-500 mb-4">
                  Suggested text links to use in emails or content.
                </p>
                
                <div className="space-y-4">
                  <div className="border rounded-md p-2">
                    <p className="text-sm mb-2">
                      "Check out Explanatum for business automation tools!"
                    </p>
                    <div className="flex justify-end">
                      <button className="text-xs text-blue-600">Copy Text</button>
                    </div>
                  </div>
                  
                  <div className="border rounded-md p-2">
                    <p className="text-sm mb-2">
                      "I use Explanatum for my business planning - it's amazing!"
                    </p>
                    <div className="flex justify-end">
                      <button className="text-xs text-blue-600">Copy Text</button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AffiliateDetails;
